import React, { forwardRef, useImperativeHandle } from 'react';

import { useIntl } from 'react-intl';

import { Col, Form, Input, Row } from 'antd';
import PropTypes from 'prop-types';

import BoxContainer from 'components/BoxContainer';
import Title from 'components/BoxContainer/components/Title';
import FormSelect from 'components/Form/components/FormSelect';
import { SortableComponent } from 'components/Sortable/SortableComponent';

import DropDownTriggers from 'containers/DropDownTriggers';

import { KeyValueWidget } from '@src/components/KeyValueWidget';
import DivisionsSelector from '@src/containers/DivisionsSelector';

import IntlMessages from 'util/IntlMessages';

import { DASHBOARDS_FORMS } from '../../constants';
import styles from './styles.module.less';
import useTableForm from './hooks/useTableForm';
import LocationCombiner from '@src/components/Location/LocationCombiner';

const TableForm = forwardRef(({ tableId, boardId, divId }, formRef) => {
  const intl = useIntl();

  const {
    form,
    mainLoading,
    tableColumns,
    selectedEventTypeId,
    triggerIds,
    eventTypes,
    eventLoading,
    divisionPerEventType,
    eventTypesFromSchedule,
    ColumnItem,
    buildBody,
    onChangeTriggers,
    handleFormIdChange,
    handleTriggersClear,
    onSortableMove,
    getImperativeHandle,
    divisions,
    handleDivisionChange,
  } = useTableForm({ tableId, boardId, divId });

  useImperativeHandle(formRef, getImperativeHandle, [getImperativeHandle]);

  return (
    <Form
      form={form}
      onFieldsChange={buildBody}
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 24 }}
      name={DASHBOARDS_FORMS.EVENT_TABLE}
      initialValues={{
        id: tableId ?? null,
        formId: selectedEventTypeId ?? null,
        tableFormData: {},
        title: '',
        triggerIds: triggerIds ?? [],
        divisionId: null,
        location: null,
      }}
    >
      <BoxContainer content loading={mainLoading}>
        <Row gutter={16}>
          <Col span={24}>
            <Title.LabelForm
              required
              value={<IntlMessages id="dashboards.events.table.form.table.name" />}
            />
            <Form.Item
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'form.required',
                  }),
                },
              ]}
              name="title"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Title.SubTitle
              className="gx-pb-3"
              value={intl.formatMessage({ id: 'dashboards.chart.form.counter.filter.by' })}
            />
          </Col>
          {!divId && (
            <Col span={24} className="gx-pb-3">
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    required
                    value={intl.formatMessage({ id: 'common.select.divisions' })}
                  />
                }
                value={
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.divisionId !== currentValues.divisionId
                    }
                  >
                    {({ getFieldValue }) => {
                      return (
                        <Form.Item name="divisionId" className="gx-m-0">
                          <DivisionsSelector
                            divisionIds={getFieldValue('divisionId')}
                            showAllOption={true}
                            allLabelId="divisionSelector.allDivisions"
                            allValue={divisions.map(division => division.value)}
                            onChange={handleDivisionChange}
                            mode="single"
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                }
              />
            </Col>
          )}
          <Col span={24} className="gx-mb-3">
            <KeyValueWidget
              label={
                <>
                  <Title.LabelForm value={intl.formatMessage({ id: 'general.locations' })} />
                </>
              }
              value={
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.divisionId !== currentValues.divisionId
                  }
                >
                  {({ getFieldValue, setFieldValue }) => {
                    return (
                      <Form.Item name="location">
                        <LocationCombiner
                          value={getFieldValue('location') ?? null}
                          divisionId={divId || getFieldValue('divisionId')}
                          onChange={value => setFieldValue('location', value)}
                          showAddButton={false}
                          emptyOptionLabel="general.all"
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              }
            />
          </Col>
          <Col span={24}>
            <Title.LabelForm required value={<IntlMessages id="dashboards.chart.form.form" />} />
            <Form.Item
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'form.required',
                  }),
                },
              ]}
              name="formId"
            >
              <FormSelect
                onChange={handleFormIdChange}
                loading={eventLoading}
                options={eventTypes}
                showSearch
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="triggerIds">
              <Title.LabelForm value={<IntlMessages id="general.eventTypes" />} />
              <DropDownTriggers
                mode="multiple"
                allowClear
                onChange={onChangeTriggers}
                division={divisionPerEventType}
                eventType={selectedEventTypeId}
                defaultValue={triggerIds}
                value={triggerIds}
                onClear={handleTriggersClear}
                extraTriggers={eventTypesFromSchedule}
                disabled={!selectedEventTypeId}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Title.SubTitle value={<IntlMessages id="dashboards.events.table.form.subtitle" />} />
            <p className="gx-m-0">
              <IntlMessages id="dashboards.events.table.form.description" />
            </p>
            <div className={styles.notAllowedMessage}>
              <span className="material-symbols-outlined">info</span>
              <span>
                <IntlMessages id="dashboards.events.table.form.notAllowed" />
              </span>
            </div>
            <SortableComponent
              items={tableColumns}
              Component={ColumnItem}
              onMove={onSortableMove}
              zIndex={1000}
            />
          </Col>
        </Row>
      </BoxContainer>
      <Form.Item name="tableFormData" hidden />
      <Form.Item name="id" hidden />
    </Form>
  );
});

TableForm.propTypes = {
  tableId: PropTypes.string,
  boardId: PropTypes.string,
  divId: PropTypes.string,
};

export default TableForm;
